import { Authenticator } from '@aws-amplify/ui-react';
import { Amplify, Auth } from 'aws-amplify';
import React, { useEffect, useState } from 'react';
import { envConfig } from '../../configuration/aws-exports';
import { fetchFeatureFlags } from 'src/config/featureFlags';
import { FullPageSpinner } from 'src/common/FullPageSpinner';

/**
 * Props for the AuthProvider component.
 */
interface AuthProviderProps {
  children: JSX.Element;
}

const REDIRECT_STORAGE_KEY = 'redirectUrl';

/**
 * Narrows a stored redirect target to a same-origin path, so the redirect cannot be talked
 * into leaving the origin. Rejects protocol-relative (`//host`) and backslash (`/\host`)
 * forms, which browsers normalise to an absolute URL. This is what keeps the redirect below
 * clear of the open-redirect finding that previously disabled it.
 */
export const toSafeRedirectPath = (value: string | null): string | null => {
  if (!value?.startsWith('/')) return null;
  return value.startsWith('//') || value.startsWith('/\\') ? null : value;
};

/**
 * AuthProvider component handles authentication and configuration for the application.
 * It uses AWS Amplify for authentication and manages the authentication state.
 *
 * @param {AuthProviderProps} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [isConfiguring, setIsConfiguring] = useState(true);

  useEffect(() => {
    /**
     * Configures Amplify and checks the current authentication state.
     * If not authenticated, it initiates federated sign-in.
     */
    const configure = async () => {
      try {
        Amplify.configure(envConfig);
        await Auth.currentAuthenticatedUser();
        // Warm the feature-flag cache so AI_ASSISTANT is available synchronously later.
        await fetchFeatureFlags();
        // Orion is the standard theme — set unconditionally before first paint.
        document.body.classList.add('orion-ui');
        setIsConfiguring(false);
      } catch (error) {
        console.error(error);
        const currentPath = window.location.pathname + window.location.search;
        sessionStorage.setItem(REDIRECT_STORAGE_KEY, currentPath);
        Auth.federatedSignIn({
          customProvider: 'AmazonFederate',
          // Carried through the IdP round-trip in the OAuth `state` parameter, so a deep link
          // survives login even when sessionStorage is a fresh context.
          customState: currentPath,
        });
      }
    };
    configure();
  }, []);

  useEffect(() => {
    /**
     * Handles redirection after successful authentication.
     */
    if (!isConfiguring) {
      const stored = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
      if (stored) {
        sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
        const target = toSafeRedirectPath(stored);
        // Only redirect when we are not already there, otherwise this reloads in a loop.
        if (target && target !== window.location.pathname + window.location.search) {
          window.location.replace(target);
        }
      }
    }
  }, [isConfiguring]);

  if (isConfiguring) {
    return <FullPageSpinner />;
  }

  return (
    <React.Suspense fallback={<FullPageSpinner />}>
      <Authenticator.Provider>{children}</Authenticator.Provider>
    </React.Suspense>
  );
};
